import {
  Flex,
  Menu,
  Tooltip,
  UnstyledButton,
  useMantineTheme,
} from '@mantine/core';
import { useClientTerminology } from '@/views/list/NetworkTerms';
import type { FC, ReactElement } from 'react';
import classes from './DetailView.module.css';
import { Icon, IconButton } from '@/components';
import {
  MoreVertical,
  type Icon as FeatherIcon,
  ChevronLeft,
} from 'react-feather';
import { sanitizeTestId } from '@/app/utils';

interface MenuItem {
  label: string;
  onClick: () => void;
  icon?: FeatherIcon;
  color?: string;
  disabled?: boolean;
  testId: string;
}
interface DetailViewHeaderProps {
  heading: string;
  onBack?: () => void;
  subHeading?: string | ReactElement;
  menuItems?: MenuItem[];
}

export const DetailViewHeader: FC<DetailViewHeaderProps> = ({ ...props }) => {
  const theme = useMantineTheme();
  const intl = useClientTerminology();

  return (
    <Flex
      className={classes.header}
      direction="column"
      align="flex-start"
      style={{
        color: `${theme.other.mainColor.foreground.primary}`,
      }}
    >
      {props.onBack && (
        <UnstyledButton onClick={props.onBack} className={classes.backButton}>
          <ChevronLeft size={16} />
          {intl.formatMessage({ id: 'action.back' })}
        </UnstyledButton>
      )}
      <Flex align="center" gap={16} w="100%">
        <Flex className={classes.headings} direction="column">
          {props.subHeading && (
            <span
              style={{
                ...theme.other.typography.variants.bodySmallRegular,
                color: theme.other.mainColor.foreground.secondary,
              }}
            >
              {props.subHeading}
            </span>
          )}
          <Tooltip label={props.heading} withArrow openDelay={500}>
            <div
              style={theme.other.typography.variants.h4}
              className={classes.heading}
              data-testid="detail-view-header-heading"
            >
              {props.heading}
            </div>
          </Tooltip>
        </Flex>
        {props.menuItems && (
          <Menu shadow="md" width={200} position="bottom-start" offset={-10}>
            <Menu.Target>
              <IconButton
                Component={MoreVertical}
                data-testid="detail-view-header-menu"
              />
            </Menu.Target>

            <Menu.Dropdown>
              {props.menuItems.map((item) => (
                <Menu.Item
                  key={item.label}
                  onClick={item.onClick}
                  leftSection={
                    item.icon && (
                      <Icon
                        color={item.color}
                        Component={item.icon}
                        size={14.5}
                      />
                    )
                  }
                  color={item.color}
                  disabled={item.disabled}
                  data-testid={`detail-view-header-menu-item-${sanitizeTestId(item.testId)}`}
                >
                  {item.label}
                </Menu.Item>
              ))}
            </Menu.Dropdown>
          </Menu>
        )}
      </Flex>
    </Flex>
  );
};
